import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/nurseryApi";
import { PencilSimple, Trash, X, Phone, EnvelopeSimple, MapPin, Globe, ShieldCheck, ArrowRight, Star, ChatCircleDots } from "@phosphor-icons/react";
import BackToOverview from "../../components/BackToOverview";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const { id, nursery_name, owner_name, phone, address, website, is_admin } = editing;
    await api.put(`/admin/users/${id}`, { nursery_name, owner_name, phone, address, website, is_admin });
    setEditing(null); load();
  };
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    await api.delete(`/admin/users/${u.id}`);
    load();
  };

  const filtered = users.filter((u) => {
    const s = (u.email + u.nursery_name + u.owner_name + (u.address || "")).toLowerCase();
    return s.includes(q.toLowerCase());
  });

  return (
    <div data-testid="admin-users-page" className="fade-up">
      <BackToOverview />
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--secondary)" }}>Customer book</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 14, marginBottom: 24 }}>
        <div>
          <h1 className="page-title" style={{ marginTop: 8 }}>Users & Accounts</h1>
          <p style={{ color: "var(--muted-fg)", marginTop: 6, fontSize: 14.5 }}>{users.length} accounts · contact info for marketing & outreach.</p>
        </div>
        <input className="input" placeholder="Search nursery, owner, address…" value={q} onChange={(e) => setQ(e.target.value)} style={{ maxWidth: 320 }} data-testid="user-search-input" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 16 }}>
        {filtered.map((u) => (
          <div key={u.id} className="card" style={{ padding: 22 }} data-testid={`user-card-${u.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <h3 className="font-display" style={{ fontSize: 22 }}>{u.nursery_name}</h3>
                  {u.is_admin && <span className="badge badge-warn"><ShieldCheck size={11} weight="bold" /> admin</span>}
                  {u.is_founding && <span className="badge badge-success"><Star size={11} weight="fill" /> Founding</span>}
                  {u.chatbot_enabled && <span className="badge badge-soft"><ChatCircleDots size={11} weight="bold" /> Chatbot</span>}
                </div>
                <div style={{ fontSize: 13, color: "var(--muted-fg)", marginTop: 2 }}>{u.owner_name}</div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <Link to={`/admin/users/${u.id}`} className="btn btn-ghost" style={{ minHeight: 34, padding: "6px 10px" }} title="Open full detail" data-testid={`open-user-${u.id}`}>
                  <ArrowRight size={16} weight="bold" />
                </Link>
                <button className="btn btn-ghost" style={{ minHeight: 34, padding: "6px 10px" }} onClick={() => setEditing({ ...u })} title="Quick edit" data-testid={`edit-user-${u.id}`}>
                  <PencilSimple size={16} weight="bold" />
                </button>
                <button className="btn btn-ghost" style={{ minHeight: 34, padding: "6px 10px" }} onClick={() => del(u)} title="Delete" disabled={u.is_admin} data-testid={`del-user-${u.id}`}>
                  <Trash size={16} weight="bold" />
                </button>
              </div>
            </div>

            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8, fontSize: 13.5 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-soft)" }}>
                <EnvelopeSimple size={15} weight="fill" color="var(--primary)" />
                <a href={`mailto:${u.email}`} style={{ color: "var(--text)", textDecoration: "underline" }}>{u.email}</a>
              </div>
              {u.phone && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-soft)" }}>
                  <Phone size={15} weight="fill" color="var(--primary)" />
                  <a href={`tel:${u.phone}`} style={{ color: "var(--text)" }}>{u.phone}</a>
                </div>
              )}
              {u.address && (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, color: "var(--text-soft)" }}>
                  <MapPin size={15} weight="fill" color="var(--primary)" style={{ marginTop: 2, flexShrink: 0 }} />
                  <span>{u.address}</span>
                </div>
              )}
              {u.website && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-soft)" }}>
                  <Globe size={15} weight="fill" color="var(--primary)" />
                  <a href={u.website} target="_blank" rel="noreferrer" style={{ color: "var(--secondary)", textDecoration: "underline" }}>{u.website.replace(/^https?:\/\//, "")}</a>
                </div>
              )}
            </div>

            <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--border)", display: "flex", gap: 10, fontSize: 12, color: "var(--muted-fg)", flexWrap: "wrap" }}>
              <span><b style={{ color: "var(--text)" }}>{u.scans_count}</b> scans</span>
              <span>·</span>
              <span><b style={{ color: "var(--text)" }}>{u.inventory_count}</b> items</span>
              <span>·</span>
              <span><b style={{ color: "var(--text)" }}>{u.leads_count}</b> leads</span>
              <span style={{ marginLeft: "auto", fontSize: 11 }}>joined {new Date(u.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="card" style={{ padding: 32, textAlign: "center", color: "var(--muted-fg)" }}>No users match your search.</div>
        )}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={`Edit ${editing.nursery_name}`}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Nursery" value={editing.nursery_name} onChange={(v) => setEditing({ ...editing, nursery_name: v })} testid="edit-nursery" />
            <Field label="Owner" value={editing.owner_name} onChange={(v) => setEditing({ ...editing, owner_name: v })} testid="edit-owner" />
            <Field label="Phone" value={editing.phone || ""} onChange={(v) => setEditing({ ...editing, phone: v })} testid="edit-phone" />
            <Field label="Website" value={editing.website || ""} onChange={(v) => setEditing({ ...editing, website: v })} testid="edit-website" />
            <div style={{ gridColumn: "1 / -1" }}>
              <Field label="Address" value={editing.address || ""} onChange={(v) => setEditing({ ...editing, address: v })} testid="edit-address" />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, gridColumn: "1 / -1", padding: 12, background: "var(--bg-alt)", borderRadius: 12 }}>
              <input type="checkbox" checked={!!editing.is_admin} onChange={(e) => setEditing({ ...editing, is_admin: e.target.checked })} data-testid="edit-is-admin" />
              <span><b>Admin access</b> — can manage all users and blog posts</span>
            </label>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
            <button className="btn btn-outline" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={save} data-testid="save-user-btn">Save changes</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(42,51,42,0.45)", backdropFilter: "blur(4px)", display: "grid", placeItems: "center", zIndex: 60, padding: 16 }}>
      <div className="card-soft" style={{ background: "var(--surface)", padding: 24, width: "100%", maxWidth: 720, maxHeight: "90vh", overflow: "auto", borderRadius: 22 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 className="font-display" style={{ fontSize: 24 }}>{title}</h3>
          <button className="btn btn-ghost" onClick={onClose} data-testid="modal-close"><X size={18} weight="bold" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
